import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";

import { useCallback, useEffect, useRef, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";

import { green, grey } from "@mui/material/colors";
import { GlobalState } from "context/GlobalContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { publishTableColumn, selectedShootCostingPagepath } from "components/PimDashboard/data";
import { costingColumns, shootCostingUrlKey, CreatedAtColumn, totalCostColumn } from "../CreateShootCosting/data";
import { converAttrToDatagridCol, createdByColumns, imageTableColumn, publishColumns, styleImageColumn } from "data/globalData";
import { FileDownloadRounded, FilterAltRounded, PlaylistAddRounded } from "@mui/icons-material";
import FileSaver from "file-saver";
import FilterModal from "./FilterModal/FilterModal";
import AddColumnModal from "./AddColumnModal/AddColumnModal";
import { allowAccess } from "utils/userAccessFun";
import { custom, departments } from "data/authData";
import { AuthState } from "context/AuthContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { generateCostingDetailsExcel, lsFilterkeyStr } from "./data";
import _ from "lodash";
import moment from "moment";

export function setStylesCustomFun(prev, newRows) {
	const map = {};

	const result = [...prev, ...newRows]?.reduce((acc, row) => {
		if (map[row.shoot_costing_id]) return acc;
		else {
			acc.push(row);
			map[row.shoot_costing_id] = true;
		}
		return acc;
	}, []);
	return result;
}

const pageFilterLgKey = "studio_costing_table_attr";

const ShootCostingDashboard = () => {
	const gridRef = useRef();
	const { user } = AuthState();
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const [tableData, setTableData] = useState([]);
	const [displayImg, setDisplayImg] = useState(false);
	const [tableStatusCount, setTableStatusCount] = useState({
		total: 0,
		pending: 0,
		published: 0,
	});
	const [allColumns, setAllColumns] = useState([]);
	const [allColumnsObj, setAllColumnsObj] = useState({});
	const [publishModal, setPublishModal] = useState(false);
	const [filterModal, setFilterModal] = useState(false);
	const [addColumnModal, setAddColumnModal] = useState(false);

	const defColumns = [...createdByColumns.map((row) => converAttrToDatagridCol(row))];

	const [dgTablColumns, setDgTableColumns] = useState(defColumns);

	const [attrColumns, setAttrColumns] = useState([]);
	const [defAttrColumns, setDefAttrColumns] = useState([]);

	const [filterColumns, setFilterColumns] = useState([]);
	const [filterColumnVal, setFilterColumnVal] = useState({});
	const [offset, setOffset] = useState(0);
	const [recordsLimit] = useState(20);
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const defAttrColumns = [...Object.values(costingColumns), totalCostColumn, CreatedAtColumn];
		const allColumns = [...defAttrColumns, ...createdByColumns, ...Object.values(publishColumns)];

		const allColumnsObj = allColumns?.reduce((acc, row) => {
			acc[row.key] = row;
			return acc;
		}, {});
		setAllColumns(allColumns);
		setAllColumnsObj(allColumnsObj);
		setAttrColumns(() => {
			if (localStorage.getItem(pageFilterLgKey)) {
				return JSON.parse(localStorage.getItem(pageFilterLgKey))
					?.map((row) => {
						return { ...allColumnsObj?.[row] };
					})
					.map((row) => converAttrToDatagridCol(row));
			} else return defAttrColumns?.map((row) => converAttrToDatagridCol(row));
		});
		setFilterColumns(() => {
			let final = [];
			if (localStorage.getItem(lsFilterkeyStr)) {
				const localStorageObj =
					typeof JSON.parse(localStorage.getItem(lsFilterkeyStr)) === "object"
						? JSON.parse(localStorage.getItem(lsFilterkeyStr))
						: {};
				allColumns?.forEach((row) => {
					if (localStorageObj?.[row?.key]) final.push(row);
				});
				return final;
			}
		});
		setFilterColumnVal(() => {
			if (localStorage.getItem(lsFilterkeyStr)) {
				return JSON.parse(localStorage.getItem(lsFilterkeyStr));
			} else {
				return {};
			}
		});
	}, []);

	const handleCloseFilterModal = () => {
		setFilterModal(false);
	};

	const handleCloseAddColumnModal = () => {
		setAddColumnModal(false);
	};

	const handleFilterClick = () => {
		setFilterModal(true);
	};

	const handleAddColumnClick = () => {
		setAddColumnModal(true);
	};

	const fetchtableData = async (offset, limit) => {
		try {
			const extraColumns = attrColumns?.reduce((acc, row) => {
				acc[row.field] = true;
				return acc;
			}, {});

			const { data } = await axios.post(`/api/studio/shoot_costings/${offset}`, {
				filter: filterColumnVal,
				extraColumns,
				limit,
			});
			setTableData((prev) => setStylesCustomFun(prev, data?.result?.rows ?? []));
			setTableStatusCount(data.result.count[0]);
			setOffset((prev) => prev + recordsLimit);
			return data.result || [];
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error) });
			return [];
		}
	};

	const loadMoreData = useCallback(
		_.throttle(async () => {
			if (loading || !hasMore) return;
			if (tableData?.length > 100) return;
			setLoading(true);

			const newRows = await fetchtableData(offset, recordsLimit);

			if (newRows?.rows?.length < recordsLimit) setHasMore(false);
			setLoading(false);
		}, 2000),
		[loading, hasMore, offset]
	);

	useEffect(() => {
		const gridElement = gridRef.current;
		if (!gridElement) return;
		const scrollContainer = gridElement.querySelector(".MuiDataGrid-virtualScroller");
		if (!scrollContainer) return;
		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
			if (scrollHeight - scrollTop <= clientHeight + 100) {
				loadMoreData();
			}
		};
		scrollContainer.addEventListener("scroll", handleScroll);
		return () => {
			scrollContainer.removeEventListener("scroll", handleScroll);
		};
	}, [loadMoreData]);

	useEffect(() => {
		const fetchInitialData = async () => {
			setLoadingOpen(true);
			setDgTableColumns([
				// downLoadTableColumn(handleDownloadCostingClick, true),
				// imageTableColumn(styleImageColumn),
				...attrColumns,
				...defColumns,
				publishTableColumn(handlePublishClick, !allowAccess(custom.SHOOT_COSTING_PUBLISHER, user, departments.SOURCING.key)),
			]);
			const result = await fetchtableData(0, recordsLimit);
			setTableData(result.rows);
			setTableStatusCount(result.count[0]);
			setOffset((prev) => prev + recordsLimit);
			setHasMore(result.rows.length === recordsLimit);
			setLoadingOpen(false);
		};
		if (allColumns?.length) fetchInitialData();
	}, [filterColumns, attrColumns]);

	const navigate = useNavigate();

	const handleImageSwitchChange = (checked) => {
		setDisplayImg(checked);
	};

	const handleDgRowSelect = async (data) => {
		navigate(`/${selectedShootCostingPagepath}?${shootCostingUrlKey}=${data.shoot_costing_id}`);
	};
	const handleDownloadClick = async () => {
		setLoadingOpen(true);
		try {
			await axios({
				url: `/api/sourcing/download_all_costings`,
				method: "POST",
				responseType: "blob",
				data: { filter: filterColumnVal },
			}).then((res) => {
				const url = URL.createObjectURL(res.data);
				FileSaver.saveAs(url, `Shoot_Costings_${new Date().toISOString()}.xlsx`);
				setLoadingOpen(false);
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error) });
			setLoadingOpen(false);
		}
	};
	const handleDownloadCostingClick = async (e, costData) => {
		e.stopPropagation();
		setLoadingOpen(true);
		try {
			const result = await axios.get(`/api/sourcing/costing_full_data/${costData.shoot_costing_id}`);
			console.log("jhfgvhbjoiuyghbjiu", result);
			const res = await generateCostingDetailsExcel(result.data.result);
			if (res) {
				setLoadingOpen(false);
			}
		} catch (error) {
			console.log("qwertyuiop", error);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		}
	};
	const handlePublishClick = async (e, styleData) => {
		e.stopPropagation();
		setLoadingOpen(true);
		try {
			const res = await axios.post(`/api/sourcing/publish_costing`, {
				shoot_costing_id: styleData.shoot_costing_id,
				published_date: moment().format("YYYY-MM-DD"),
			});
			setTableData((prev) => {
				return prev.map((tableRow) => {
					if (tableRow.shoot_costing_id === styleData.shoot_costing_id) {
						return { ...tableRow, ...res.data.result };
					} else return tableRow;
				});
			});
			setTableStatusCount((prev) => ({ ...prev, published: +prev.published + 1, pending: prev.pending - 1 }));
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Costing Published Successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		}
	};

	return (
		<Stack width="100%" pt={1}>
			<Box display="flex" justifyContent="space-around">
				{/* left section */}
				<Stack spacing={1} width="28%">
					<Box display="flex" columnGap={1} pt={1} flexWrap="wrap" alignItems="center" rowGap={"5px"}>
						<Button
							variant="contained"
							size="small"
							startIcon={<FilterAltRounded />}
							onClick={handleFilterClick}
							sx={{ textTransform: "capitalize" }}>
							Filter
						</Button>
						:
						{!filterColumns?.length && (
							<Typography variant="body1" fontSize={16} color={grey[700]}>
								None
							</Typography>
						)}
						{filterColumns?.map((row) => {
							return (
								<Chip
									label={`${row.name} : ${
										Array.isArray(filterColumnVal[row?.key])
											? filterColumnVal[row?.key]?.join(", ")
											: filterColumnVal?.[row?.key]
									}`}
									size="small"
								/>
							);
						})}
					</Box>
					<Box>
						<Button
							variant="contained"
							sx={{ textTransform: "capitalize", mr: 1 }}
							size="small"
							onClick={handleAddColumnClick}
							startIcon={<PlaylistAddRounded />}>
							Manage Columns
						</Button>
						<Button
							variant="contained"
							size="small"
							sx={{ textTransform: "capitalize" }}
							onClick={handleDownloadClick}
							startIcon={<FileDownloadRounded />}>
							Download
						</Button>
					</Box>
				</Stack>
				{/* right section */}
				<Box width="68%"></Box>
			</Box>
			<Box display="flex" justifyContent="space-between" alignItems="center" pt={1}>
				<Box display="flex" alignItems="center">
					{/* <Typography variant="h6">Images</Typography>
					<Switch checked={displayImg} onChange={(e) => handleImageSwitchChange(e.target.checked)} sx={{ mt: 1 }} /> */}
				</Box>
				<Box display="flex" alignItems="center" columnGap={1}>
					<Typography variant="body1">
						Total: <span style={{ color: grey[700] }}>{tableStatusCount.total}</span>
					</Typography>
					<Divider orientation="vertical" flexItem />
					<Typography variant="body1">
						Published: <span style={{ color: green[600] }}>{tableStatusCount.published ?? 0}</span>
					</Typography>
					<Divider orientation="vertical" flexItem />
					<Typography variant="body1" fontWeight={600}>
						Pending: <span style={{ color: "red" }}>{tableStatusCount.pending ?? 0}</span>
					</Typography>
				</Box>
			</Box>

			<Box height="60vh" ref={gridRef}>
				<DataGrid
					rows={tableData ? tableData : []}
					key={displayImg}
					columns={dgTablColumns ?? []}
					sx={{
						// minWidth: "100%"
						border: "none",
						"& .MuiDataGrid-row": {
							// borderBottom: "1px solid lightgray",
							cursor: "pointer",
							border: "none",
						},
						"& .MuiDataGrid-columnHeader": {
							cursor: "pointer",
							background: grey[300],
						},
						"& .MuiDataGrid-columnHeaders": {
							background: grey[300],
							// mb: 2,
							border: "none",
							borderRadius: 1,
							// padding: "10px 0px",
						},
						"& .MuiDataGrid-footer": {
							background: grey[300],
							// mb: 2,
							border: "none",
							borderRadius: 1,
							// padding: "10px 0px",
						},
					}}
					columnHeaderHeight={35}
					rowHeight={displayImg ? 70 : 50}
					getRowId={(row) => row?.shoot_costing_id}
					onRowClick={(params, e) => handleDgRowSelect(params.row)}
					initialState={{
						columns: {
							columnVisibilityModel: {
								[styleImageColumn.key]: displayImg,
							},
						},
					}}
					disableRowSelectionOnClick
					hideFooter={true}
					pagination={false}
					disableColumnFilter
					disableColumnSelector
					disableAutosize
				/>
				{/* <Box sx={{ minHeight: "20px" }}> */}
				{loading && (
					<Box display="flex" sx={{ background: grey[400], borderRadius: 2, p: 1 }} alignItems="center">
						<Typography variant="body2" color={grey[900]}>
							Loading...
						</Typography>
						<CircularProgress size="18px" sx={{ transform: "scale(0.4)" }} />
					</Box>
				)}
			</Box>

			<FilterModal
				modal={filterModal}
				handleCloseModal={handleCloseFilterModal}
				filterColumns={filterColumns}
				setFilterColumns={setFilterColumns}
				filterColumnVal={filterColumnVal}
				setFilterColumnVal={setFilterColumnVal}
				localStorageStr={lsFilterkeyStr}
				allColumns={allColumns}
			/>
			<AddColumnModal
				modal={addColumnModal}
				handleCloseModal={handleCloseAddColumnModal}
				attrColumns={attrColumns}
				setAttrColumns={setAttrColumns}
				localStorageStr={pageFilterLgKey}
				allColumns={allColumns}
				allColumnsObj={allColumnsObj}
				defaultColumns={[...Object.values(costingColumns), ...defAttrColumns]}
			/>
		</Stack>
	);
};

export default ShootCostingDashboard;
